
import { useState, useEffect, useRef } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import { Hourglass, CheckCircle2, XCircle, Trash2, AlertTriangle, RefreshCw, Plus, Search, X, ClipboardList } from "lucide-react";

// ─── AP DESIGN TOKENS (matching ProjectList.js exactly) ──────────────────────
const T = {
  violet:      "#5C2D91",
  violetMid:   "#7B3FB5",
  violetLight: "#EDE6F7",
  violetFaint: "#F7F3FD",
  red:         "#C8102E",
  redLight:    "#FCEAED",
  orange:      "#E8640A",
  orangeLight: "#FEF0E6",
  gold:        "#C87800",
  goldLight:   "#FEF5DC",
  green:       "#1A7A42",
  greenLight:  "#E2F5EC",
  bg:          "#F4F0FA",
  surface:     "#FFFFFF",
  surfaceAlt:  "#FAF8FD",
  dark:        "#1A0A2E",
  muted:       "#6B5A8A",
  faint:       "#C4B8DC",
  border:      "rgba(92,45,145,0.1)",
  borderSolid: "#E8E0F5",
};

// ─── Phase step avatar ────────────────────────────────────────────────────────
function PhaseAvatar({ index }) {
  const gradients = [
    `linear-gradient(135deg,${T.violet},${T.violetMid})`,
    `linear-gradient(135deg,${T.green},#22A458)`,
    `linear-gradient(135deg,${T.gold},#E69A0B)`,
    `linear-gradient(135deg,${T.red},#E02E49)`,
    `linear-gradient(135deg,${T.violetMid},#9B6FD0)`,
    `linear-gradient(135deg,${T.orange},#F07830)`,
  ];
  return (
    <div style={{
      width: 32, height: 32, borderRadius: 9,
      background: gradients[index % gradients.length],
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 800, fontSize: 13, color: "#fff",
      flexShrink: 0, userSelect: "none",
      boxShadow: `0 2px 6px rgba(92,45,145,0.25)`,
    }}>
      {index + 1}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function PhaseMaster() {
  function Modal({ modal, onClose }) {
  if (!modal) return null;
  const isConfirm = modal.type === "confirm";
  const isError   = modal.type === "error";
  const iconMap   = { success: <CheckCircle2 size={22} />, error: <XCircle size={22} />, confirm: <Trash2 size={22} />, validation: <AlertTriangle size={22} /> };
  const colorMap  = {
    success:    { accent: T.green,  light: T.greenLight,  border: "#A7F3D0" },
    error:      { accent: T.red,    light: T.redLight,    border: "#FECDD3" },
    confirm:    { accent: T.red,    light: T.redLight,    border: "#FECDD3" },
    validation: { accent: T.orange, light: T.orangeLight, border: "#FED7AA" },
  };
  const c = colorMap[modal.type] || colorMap.success;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(26,10,46,0.45)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, animation: "pmFadeIn 0.18s ease",
    }}>
      <style>{`@keyframes pmFadeIn { from { opacity:0; transform:scale(0.94) } to { opacity:1; transform:scale(1) } }`}</style>
      <div style={{
        background: T.surface, borderRadius: 20, width: "100%", maxWidth: 420,
        boxShadow: "0 20px 60px rgba(92,45,145,0.22), 0 4px 16px rgba(0,0,0,0.10)",
        border: `1px solid ${T.borderSolid}`, overflow: "hidden",
      }}>
        <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${c.accent})` }} />
        <div style={{ padding: "28px 28px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              background: c.light, border: `1.5px solid ${c.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
            }}>{iconMap[modal.type]}</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: T.dark, lineHeight: 1.25 }}>{modal.title}</div>
            </div>
          </div>
          <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.65, margin: "0 0 24px", fontWeight: 400 }}>
            {modal.message}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            {isConfirm && (
              <button onClick={onClose} style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 600,
                background: T.surfaceAlt, color: T.muted,
                border: `1.5px solid ${T.borderSolid}`, cursor: "pointer",
                fontFamily: "Montserrat, sans-serif",
              }}>Cancel</button>
            )}
            <button
              onClick={() => { if (modal.onConfirm) modal.onConfirm(); onClose(); }}
              style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 700,
                background: (isConfirm || isError)
                  ? `linear-gradient(135deg,${T.red},#E02040)`
                  : `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none", cursor: "pointer",
                fontFamily: "Montserrat, sans-serif",
                boxShadow: `0 4px 14px ${(isConfirm || isError) ? T.red : T.violet}40`,
              }}
            >{isConfirm ? "Delete" : "OK"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

  // ── state — ALL UNCHANGED ──
  const [phases,     setPhases]     = useState([]);
  const [phaseTitle, setPhaseTitle] = useState("");
  const [editingId,  setEditingId]  = useState(null);
  const [search,     setSearch]     = useState("");
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [hov,        setHov]        = useState(null);
  const [productCategories, setProductCategories] = useState([]);
const [selectedCategory,  setSelectedCategory]  = useState("");
 const [modal, setModal] = useState(null);
const showModal = (type, title, message, onConfirm = null) =>
  setModal({ type, title, message, onConfirm });
const closeModal = () => setModal(null);
  const [page,       setPage]       = useState(1);
const PER_PAGE = 10;
const formRef = useRef(null);
const [refreshing, setRefreshing] = useState(false);

  // ── fetch — UNCHANGED ──
  const fetchPhases = async () => {
    try {
      setLoading(true);
      const token  = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res    = await API.get("/admin/phases", config);
      if (res.data.success) setPhases(res.data.data || []);
    } catch (err) {
      console.error("Error loading phases:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPhases(); }, []);

  useEffect(() => {
  API.get("/admin/product-categories")
    .then((res) => { setProductCategories(res.data.data || []); })
    .catch((err) => console.error("Failed fetching product categories:", err));
}, []);

  // ── save — UNCHANGED ──
  const handleSave = async () => {

    // validation change

    const phaseName = phaseTitle.trim();

// Required
if (!phaseName) {
  showModal("validation", "Required Field", "Please enter Phase Name.");
  return;
}


// Minimum length
if (phaseName.length < 3) {
  showModal(
    "validation",
    "Validation Error",
    "Phase name must be at least 3 characters."
  );
  return;
}

// Maximum length
if (phaseName.length > 50) {
  showModal(
    "validation",
    "Validation Error",
    "Phase name cannot exceed 50 characters."
  );
  return;
}

const isDuplicate = phases.some((p) => {
  const existingName = (p.name || p.title || "").trim().toLowerCase();
  return existingName === phaseName.toLowerCase() && p.id !== editingId;
});
if (isDuplicate) {
  showModal(
    "validation",
    "Duplicate Phase",
    "A phase with this name already exists. Please choose a different name."
  );
  return;
}

// Product Category
if (!selectedCategory) {
  showModal(
    "validation",
    "Validation Error",
    "Please select Product Category."
  );
  return;
}


    try {
      const token        = localStorage.getItem("token");
      const config       = { headers: { Authorization: `Bearer ${token}` } };
      const computedCode = "PH-" + phaseTitle.trim().slice(0, 3).toUpperCase();
     const payload = { name: phaseName, code: computedCode, status: "Active", product_category: selectedCategory };
      if (editingId) {
        const res = await API.put(`/admin/phases/${editingId}`, payload, config);
      if (res.data.success) showModal("success", "Phase Updated", "Phase updated successfully.");
      } else {
        const res = await API.post("/admin/phases", payload, config);
       if (res.data.success) showModal("success", "Phase Created", "New phase added successfully.");
      }
      setPhaseTitle(""); setEditingId(null); setShowForm(false); fetchPhases();
    } catch (err) {
     showModal("error", "Save Failed", err.response?.data?.message || "This Phase already exits ");
    }
  };

  // ── edit / delete / reset — UNCHANGED ──
  const handleEdit = (phase) => {
    setPhaseTitle(phase.name || phase.title || "");
    setEditingId(phase.id);
    setSelectedCategory(phase.product_category || "");
    setShowForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
};


  const handleDelete = (id) => {
  showModal(
    "confirm",
    "Delete Phase",
    "Are you sure you want to delete this phase? This action cannot be undone.",
    async () => {
      try {
        const token  = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res    = await API.delete(`/admin/phases/${id}`, config);
        if (res.data.success) {
          showModal("success", "Phase Deleted", "Phase deleted successfully.");
          fetchPhases();
        }
      } catch (err) {
        showModal("error", "Delete Failed", err.response?.data?.message || "Unable to delete the phase. Please try again later.");
      }
    }
  );
};

  const handleResetForm = () => { setPhaseTitle(""); setEditingId(null); setShowForm(false); setSelectedCategory(""); };
  // ── derived — UNCHANGED ──
  const filteredPhases = phases.filter((phase) => {
    const titleText = phase.name || phase.title || "";
    return titleText.toLowerCase().includes(search.toLowerCase());
  });
  const activeCount = phases.filter((p) => p.status === "Active").length;

  // ── pagination ──
  const totalPages = Math.max(1, Math.ceil(filteredPhases.length / PER_PAGE));
  const paged      = filteredPhases.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <AdminLayout>
      
         <Modal modal={modal} onClose={closeModal} />
      <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

.pm-root * { font-family: 'Montserrat', sans-serif !important; }

        .pm-row:hover { background: ${T.violetFaint} !important; }

        .pm-search:focus-within {
          border-color: ${T.violet} !important;
          box-shadow: 0 0 0 3px rgba(92,45,145,0.1) !important;
        }

        .pm-add-btn:hover {
          box-shadow: 0 6px 20px rgba(92,45,145,0.45) !important;
          transform: translateY(-1px);
        }
        .pm-add-btn { transition: all 0.18s ease !important; }

        .pm-refresh:hover { opacity: 0.85; }
        .pm-refresh { transition: opacity 0.15s; cursor: pointer; }
        @keyframes pm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 1024px) {
          .pm-kpi-strip { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 768px) {
          .pm-header     { flex-direction: column !important; gap: 14px !important; }
          .pm-kpi-strip  { grid-template-columns: repeat(2,1fr) !important; }
          .pm-filter-row { flex-direction: column !important; align-items: flex-start !important; }
          .pm-table-wrap { font-size: 12px !important; }
        }
        @media (max-width: 480px) {
          .pm-kpi-strip { grid-template-columns: 1fr 1fr !important; }
          .pm-page-pad  { padding: 14px 12px !important; }
          @keyframes pm-modal-in { from { opacity:0; transform:translateY(10px) scale(0.97); } to { opacity:1; transform:none; } }
        @keyframes pm-toast-in { from { opacity:0; transform:translateY(16px) scale(0.95); } to { opacity:1; transform:none; } }
        }
      `}</style>

      <div className="pm-root pm-page-pad" style={{ padding: "28px 32px", background: T.bg, minHeight: "100vh" }}>

        {/* ── PAGE HEADER ── */}
        <div className="pm-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${T.violet},${T.orange})` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Admin · Masters
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <h1 style={{ fontWeight: 800, fontSize: 28, color: T.dark, margin: 0, letterSpacing: "-0.5px" }}>
                Phase Master
              </h1>
              <span style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", fontSize: 9, fontWeight: 700,
                padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
                textTransform: "uppercase", boxShadow: `0 2px 8px ${T.violet}40`,
              }}>ADMIN</span>
            </div>
            <p style={{ color: T.muted, fontSize: 13, margin: 0, fontWeight: 400 }}>
              Manage project phase pipeline stages
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {/* Refresh — identical to ProjectList.js */}
<button
  className="pm-refresh"
  onClick={async () => {
    setRefreshing(true);
    const minSpin = new Promise(resolve => setTimeout(resolve, 700));
    await Promise.all([fetchPhases(), minSpin]);
    setRefreshing(false);
  }}
  disabled={refreshing}
  style={{
    width: 40, height: 40, borderRadius: 11,
    border: `1.5px solid ${T.borderSolid}`,
    background: T.surface, color: T.muted,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16, flexShrink: 0,
    cursor: refreshing ? "not-allowed" : "pointer",
    opacity: refreshing ? 0.7 : 1,
  }}
  title="Refresh"
>
<span style={{
    display: "inline-flex",
    animation: refreshing ? "pm-spin 0.7s linear infinite" : "none",
  }}><RefreshCw size={16} /></span>
</button>

            {/* Add Phase */}
            <button
              className="pm-add-btn"
              onClick={() => { if (showForm) { handleResetForm(); } else { setShowForm(true); } }}
              style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none",
                padding: "11px 22px", borderRadius: 12,
                fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: `0 4px 14px ${T.violet}40`,
                whiteSpace: "nowrap", cursor: "pointer",
              }}
            >
<Plus size={16} />
              {editingId ? "Edit Phase" : showForm ? "Close Form" : "Add Phase"}
            </button>
          </div>
        </div>

        {/* ── KPI STRIP — identical card design to ProjectList.js ── */}
        <div className="pm-kpi-strip" style={{
          display: "grid", gridTemplateColumns: "repeat(2,1fr)",
          gap: 16, marginBottom: 24,
        }}>
          {[
            { label: "Total Phases",  value: loading ? "…" : phases.length, icon: <Hourglass size={22} />, color: T.violet, bg: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: "#D8C8F5" },
            { label: "Active Phases", value: loading ? "…" : activeCount,   icon: <CheckCircle2 size={22} />, color: T.green,  bg: `linear-gradient(135deg,${T.greenLight},#EAFAF0)`,  border: "#B0E8C8" },
          ].map((s) => (
            <div key={s.label} style={{
              background: T.surface, borderRadius: 16,
              border: `1px solid ${T.borderSolid}`,
              padding: "20px 22px",
              display: "flex", alignItems: "center", gap: 14,
              boxShadow: "0 2px 12px rgba(92,45,145,0.06)",
              position: "relative", overflow: "hidden",
            }}>
              {/* Background accent circle */}
              <div style={{
                position: "absolute", right: -10, top: -10,
                width: 70, height: 70, borderRadius: "50%",
                background: s.bg, opacity: 0.6,
              }} />
              <div style={{
                width: 46, height: 46, borderRadius: 13,
                background: s.bg, border: `1.5px solid ${s.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, flexShrink: 0, position: "relative",
              }}>{s.icon}</div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {s.label}
                </div>
                <div style={{ fontWeight: 800, fontSize: 28, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </div>
              </div>
            </div>
          ))}
        </div>

    {/* ── FORM CARD ── */}
{showForm && (
  <div ref={formRef} style={{
    background: T.surface, borderRadius: 20,
            border: `1px solid ${T.borderSolid}`, overflow: "hidden",
            marginBottom: 24, boxShadow: "0 4px 24px rgba(92,45,145,0.08)",
            scrollMarginTop: 100,
          }}>
            <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />
            <div style={{ padding: "26px 28px" }}>

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: T.violetLight,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
}}><Hourglass size={17} /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>
                    {editingId ? "Edit Phase Details" : "Create New Phase"}
                  </div>
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>Fill in the details below</div>
                </div>
              </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 22 }}>
  
  {/* Phase Title */}
  <div>
    <label style={{
      fontSize: 11, fontWeight: 700, color: T.muted,
      display: "block", marginBottom: 8,
      textTransform: "uppercase", letterSpacing: "0.07em",
    }}>Phase Title</label>
    <input
      type="text"
      value={phaseTitle}
      onChange={(e) => setPhaseTitle(e.target.value)}
      placeholder="e.g. Surface Preparation"
      style={{
        width: "100%", borderRadius: 12,
        border: `1.5px solid ${T.borderSolid}`,
        padding: "12px 14px", fontSize: 14,
        color: T.dark, background: T.surfaceAlt,
        outline: "none", boxSizing: "border-box",
        transition: "border-color 0.2s",
      }}
      onFocus={(e) => e.target.style.borderColor = T.violet}
      onBlur={(e)  => e.target.style.borderColor = T.borderSolid}
    />
    {phaseTitle.trim() && (
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 11, color: T.muted }}>Auto-generated code:</span>
        <span style={{
          fontFamily: "monospace", fontSize: 12, fontWeight: 700,
          color: T.violet, background: T.violetLight,
          padding: "2px 10px", borderRadius: 6,
          border: `1px solid ${T.borderSolid}`,
        }}>
          PH-{phaseTitle.trim().slice(0, 3).toUpperCase()}
        </span>
      </div>
    )}
  </div>

  {/* Product Category */}
  <div>
    <label style={{
      fontSize: 11, fontWeight: 700, color: T.muted,
      display: "block", marginBottom: 8,
      textTransform: "uppercase", letterSpacing: "0.07em",
    }}>Product Category</label>
    <select
      value={selectedCategory}
      onChange={(e) => setSelectedCategory(e.target.value)}
      style={{
        width: "100%", borderRadius: 12,
        border: `1.5px solid ${T.borderSolid}`,
        padding: "12px 14px", fontSize: 14,
        color: T.dark, background: T.surfaceAlt,
        outline: "none", boxSizing: "border-box",
        appearance: "none", cursor: "pointer",
        transition: "border-color 0.2s",
      }}
      onFocus={(e) => e.target.style.borderColor = T.violet}
      onBlur={(e)  => e.target.style.borderColor = T.borderSolid}
    >
      <option value="">Select Product Category</option>
      {productCategories.map((item, idx) => (
        <option key={idx} value={item.product_category}>{item.product_category}</option>
      ))}
    </select>
  </div>

</div>
       {/* Action buttons */}
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={handleSave}
                  style={{
                    background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                    color: "#fff", border: "none",
                    padding: "12px 28px", borderRadius: 12,
                    fontSize: 14, fontWeight: 700, cursor: "pointer",
                    fontFamily: "'Montserrat', sans-serif",
                    boxShadow: `0 4px 14px ${T.violet}40`,
                  }}
                >
                  {editingId ? "Update Phase" : "Save Phase"}
                </button>
                <button
                  onClick={handleResetForm}
                  style={{
                    background: T.surfaceAlt, color: T.muted,
                    border: `1.5px solid ${T.borderSolid}`,
                    padding: "12px 20px", borderRadius: 12,
                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >Cancel</button>
              </div>

            </div>
          </div>
        )}

        {/* ── MAIN TABLE CARD ── */}
        <div style={{
          background: T.surface, borderRadius: 20,
          border: `1px solid ${T.borderSolid}`, overflow: "hidden",
          boxShadow: "0 4px 24px rgba(92,45,145,0.08)",
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />

          <div style={{ padding: "20px 24px" }}>

            {/* ── TABLE TOOLBAR ── */}
            <div className="pm-filter-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>All Project Phases</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3, fontWeight: 400 }}>
                  {filteredPhases.length} of {phases.length} phases registered
                  {search && <span style={{ color: T.violet, fontWeight: 600 }}> · filtered</span>}
                </div>
              </div>

              {/* Search — matches ProjectList.js exactly */}
              <div className="pm-search" style={{
                display: "flex", alignItems: "center", gap: 8,
                background: T.surfaceAlt,
                border: `1.5px solid ${T.borderSolid}`,
                borderRadius: 12, padding: "9px 14px",
                minWidth: 240, transition: "all 0.2s",
              }}>
<span style={{ color: T.faint, display: "inline-flex" }}><Search size={15} /></span>
                <input
                  type="text"
                  placeholder="Search phase..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  style={{
                    border: "none", background: "transparent",
                    fontSize: 13, color: T.dark, outline: "none",
                    width: "100%", fontWeight: 400,
                  }}
                />
                {search && (
<button onClick={() => { setSearch(""); setPage(1); }} style={{
                    background: T.violetLight, border: "none", color: T.violet,
                    cursor: "pointer", width: 18, height: 18,
                    borderRadius: "50%", display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0,
                  }}><X size={11} /></button>
                )}
              </div>
            </div>

            {/* ── TABLE ── */}
            {loading ? (
              <div style={{ textAlign: "center", padding: "64px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
<div style={{
                  width: 56, height: 56, borderRadius: 16, background: T.violetLight,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}><Hourglass size={24} color={T.violet} /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: T.dark, marginBottom: 4 }}>Loading phases</div>
                  <div style={{ fontSize: 13, color: T.muted }}>Connecting to phase registry…</div>
                </div>
              </div>
            ) : (
              <div className="pm-table-wrap" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 500 }}>
                  <thead>
                    <tr style={{ background: T.violetFaint }}>
                      {[
                        { label: "#",            center: true  },
                      { label: "Product Category", center: false },
                        { label: "Phase Title",  center: false },
                        { label: "Actions",      center: false },
                      ].map((h) => (
                        <th key={h.label} style={{
                          fontSize: 10, fontWeight: 700, color: T.muted,
                          textTransform: "uppercase", letterSpacing: "0.09em",
                          padding: "12px 16px",
                          textAlign: h.center ? "center" : "left",
                          borderBottom: `2px solid ${T.borderSolid}`,
                          borderTop: `1px solid ${T.borderSolid}`,
                          whiteSpace: "nowrap", background: T.violetFaint,
                        }}>{h.label}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {paged.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ padding: "52px", textAlign: "center" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                            <div style={{
                              width: 64, height: 64, borderRadius: 18,
                              background: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`,
                              border: `2px dashed ${T.faint}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}><ClipboardList size={28} color={T.violet} /></div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: T.dark }}>No phases found</div>
                            <div style={{ fontSize: 13, color: T.muted, maxWidth: 280 }}>
                              {search
                                ? `No phases match "${search}". Try a different search term.`
                                : "No project phases yet. Click Add Phase to create one."}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paged.map((phase, i) => {
                        const globalIndex = (page - 1) * PER_PAGE + i;
                        const isLast = i === paged.length - 1;
                        return (
                          <tr
                            key={phase.id}
                            className="pm-row"
                            onMouseEnter={() => setHov(phase.id)}
                            onMouseLeave={() => setHov(null)}
                            style={{ background: T.surface, transition: "background 0.12s" }}
                          >
                            {/* # — same pill style as ProjectList.js but with PhaseAvatar */}
                           <td style={{ padding: "15px 16px", textAlign: "center", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
  <span style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 26, height: 26, borderRadius: 8,
    background: T.violetFaint,
    fontWeight: 700, fontSize: 11, color: T.muted,
  }}>{globalIndex + 1}</span>
</td>
                            {/* Phase code */}
                            <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                              <span style={{
  display: "inline-block",
  background: T.violetFaint,
  border: `1px solid ${T.borderSolid}`,
  color: T.violet, fontWeight: 700,
  fontSize: 12,
  padding: "4px 10px", borderRadius: 8,
  whiteSpace: "nowrap",
}}>
  {phase.product_category || "—"}
</span>
                            </td>

                            {/* Phase title — UNCHANGED data binding */}
                            <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: T.dark }}>
                                {phase.name || phase.title}
                              </div>
                            </td>

                            {/* Actions */}
                            <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  onClick={() => handleEdit(phase)}
                                  style={{
                                    background: T.violetLight, color: T.violet,
                                    border: `1px solid ${T.borderSolid}`,
                                    padding: "7px 16px", borderRadius: 10,
                                    fontSize: 12, fontWeight: 700,
                                    cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = T.violet;      e.currentTarget.style.color = "#fff"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = T.violetLight; e.currentTarget.style.color = T.violet; }}
                             ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg></button>
                                <button
                                  onClick={() => handleDelete(phase.id)}
                                  style={{
                                    background: T.redLight, color: T.red,
                                    border: "1px solid #F8C8D0",
                                    padding: "7px 16px", borderRadius: 10,
                                    fontSize: 12, fontWeight: 700,
                                    cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = T.red;      e.currentTarget.style.color = "#fff"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = T.redLight; e.currentTarget.style.color = T.red; }}
                                ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── PAGINATION — matches ProjectList.js ── */}
            {!loading && filteredPhases.length > 0 && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginTop: 16, paddingTop: 14,
                borderTop: `1px solid ${T.borderSolid}`,
                flexWrap: "wrap", gap: 12,
              }}>
                <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
                  Showing{" "}
                  <strong style={{ color: T.dark }}>
                    {Math.min((page - 1) * PER_PAGE + 1, filteredPhases.length)}–{Math.min(page * PER_PAGE, filteredPhases.length)}
                  </strong>
                  {" "}of{" "}
                  <strong style={{ color: T.dark }}>{filteredPhases.length}</strong>
                  {" "}phases
                </span>
                {totalPages > 1 && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{
                        height: 34, padding: "0 14px", borderRadius: 10,
                        border: `1.5px solid ${page === 1 ? T.borderSolid : T.violet}`,
                        background: page === 1 ? T.surface : T.violetLight,
                        color: page === 1 ? T.faint : T.violet,
                        cursor: page === 1 ? "not-allowed" : "pointer",
                        fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                      }}
                    >‹ Prev</button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
                      .map((n) => (
                        <button key={n} onClick={() => setPage(n)} style={{
                          width: 34, height: 34, borderRadius: 10,
                          border: `1.5px solid ${n === page ? T.violet : T.borderSolid}`,
                          background: n === page ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface,
                          color: n === page ? "#fff" : T.muted,
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                          boxShadow: n === page ? `0 2px 8px ${T.violet}40` : "none",
                          transition: "all 0.15s",
                        }}>{n}</button>
                      ))}

                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      style={{
                        height: 34, padding: "0 14px", borderRadius: 10,
                        border: `1.5px solid ${page === totalPages ? T.borderSolid : T.violet}`,
                        background: page === totalPages ? T.surface : T.violetLight,
                        color: page === totalPages ? T.faint : T.violet,
                        cursor: page === totalPages ? "not-allowed" : "pointer",
                        fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                      }}
                    >Next ›</button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </AdminLayout>
  );
}